/**
 * VM Templates management API service
 *
 * Provides methods for managing VM templates stored in Cloudflare R2.
 * Templates include OS images (Linux, Windows), appliances, and gateway images.
 */

import { apiClient } from './api'

// ===== Types =====

export type VMTemplateCategory = 'gateway' | 'os_linux' | 'os_windows' | 'appliance'
export type VMTemplateFormat = 'qcow2' | 'iso' | 'ova' | 'raw'

export interface VMTemplate {
  id: string // UUID
  name: string
  version: string
  description: string | null
  category: VMTemplateCategory
  format: VMTemplateFormat

  /** catalog = distribuído pela Nexxus; cluster_import = já existia no Proxmox do cliente */
  source: 'catalog' | 'cluster_import'
  /** null = catálogo global; preenchido = template próprio da organização */
  organization_id: string | null

  // Storage info — nulos em template importado do cluster (não tem artefato no R2)
  filename: string | null
  storage_path: string | null
  size_bytes: number | null
  size_human: string
  sha256_hash: string | null

  // Status flags
  is_active: boolean
  is_featured: boolean

  // OS Information
  os_type: string | null
  os_variant: string | null
  os_version: string | null

  // Resource requirements
  min_disk_gb: number
  min_memory_mb: number
  min_cpu_cores: number

  // Additional metadata
  release_notes: string | null
  download_count: number
  tags: string[]
  /** null em template importado do cluster: não há artefato para baixar */
  download_url: string | null

  // Proxmox-specific settings
  default_disk_interface: string
  default_net_interface: string
  supports_cloud_init: boolean
  supports_qemu_agent: boolean

  // Audit fields
  created_at: string
  updated_at: string
  uploaded_by_id: string // UUID
  uploaded_by_name: string

  // Cluster tracking (retornado na listagem)
  cluster_count?: number
  total_clusters?: number
}

export interface VMTemplateListResponse {
  templates: VMTemplate[]
  total: number
  categories: Record<VMTemplateCategory, number>
}

export interface VMTemplateUploadData {
  file: File
  name: string
  version: string
  category: VMTemplateCategory
  format: VMTemplateFormat
  description?: string
  os_type?: string
  os_variant?: string
  os_version?: string
  min_disk_gb?: number
  min_memory_mb?: number
  min_cpu_cores?: number
  release_notes?: string
  tags?: string // Comma-separated
  default_disk_interface?: string
  default_net_interface?: string
  supports_cloud_init?: boolean
  supports_qemu_agent?: boolean
  set_as_active?: boolean
  set_as_featured?: boolean
}

export interface VMTemplateUpdate {
  name?: string
  description?: string
  release_notes?: string
  tags?: string[]
  is_active?: boolean
  is_featured?: boolean
  min_disk_gb?: number
  min_memory_mb?: number
  min_cpu_cores?: number
  default_disk_interface?: string
  default_net_interface?: string
  supports_cloud_init?: boolean
  supports_qemu_agent?: boolean
}

// ===== Presigned Upload Types =====

export interface PresignedUploadRequest {
  name: string
  version: string
  category: VMTemplateCategory
  format: VMTemplateFormat
}

export interface PresignedUploadResponse {
  upload_url: string
  r2_key: string
  content_type: string
  expires_at: string
}

export interface ConfirmUploadData {
  r2_key: string
  sha256_hash: string
  size_bytes: number
  name: string
  version: string
  category: VMTemplateCategory
  format: VMTemplateFormat
  description?: string
  os_type?: string
  os_variant?: string
  os_version?: string
  min_disk_gb?: number
  min_memory_mb?: number
  min_cpu_cores?: number
  release_notes?: string
  tags?: string[]
  default_disk_interface?: string
  default_net_interface?: string
  supports_cloud_init?: boolean
  supports_qemu_agent?: boolean
  set_as_active?: boolean
  set_as_featured?: boolean
}

// ===== Multipart Upload Types (#934) =====
// Espelham `backend/app/schemas/vm_template.py` (MultipartUploadRequest,
// MultipartUploadResponse, ParteConcluida, CompleteMultipartUploadRequest,
// AbortMultipartUploadRequest).

export interface MultipartUploadRequest extends PresignedUploadRequest {
  file_size: number
  /** Default do backend: 100 MiB (`vm_template_multipart.DEFAULT_PART_SIZE`). */
  part_size?: number
}

export interface MultipartUploadPart {
  part_number: number
  upload_url: string
}

export interface MultipartUploadResponse {
  upload_id: string
  r2_key: string
  content_type: string
  parts: MultipartUploadPart[]
  expires_at: string
}

/** Nomes do contrato do S3 (`PartNumber`/`ETag`): é o que o backend repassa ao boto3. */
export interface MultipartParteConcluida {
  PartNumber: number
  ETag: string
}

export interface CompleteMultipartUploadData extends Omit<ConfirmUploadData, 'r2_key'> {
  upload_id: string
  r2_key: string
  parts: MultipartParteConcluida[]
}

export interface AbortMultipartUploadData {
  upload_id: string
  r2_key: string
}

/**
 * `already_gone`: o R2 respondeu `NoSuchUpload` — não havia multipart a limpar,
 * e pode ter sobrado o OBJETO completo no bucket, que o abort não remove. Quem
 * chama precisa saber para não anunciar "abortado" ao operador.
 */
export interface AbortMultipartUploadResponse {
  status: 'aborted' | 'already_gone'
}

export interface VMTemplateDownloadRequest {
  target_storage?: string // Deprecated: storage is determined by cluster's template_storage_pool config
}

export interface VMTemplateDownloadResponse {
  success: boolean
  message: string
  task_id: string | null
}

export interface VMTemplateDownloadUrlResponse {
  template_id: string
  filename: string
  download_url: string
  sha256_hash: string
  size_bytes: number
}

// ===== API Functions =====

const vmTemplatesApi = {
  // ===== Template Management =====

  /**
   * Upload a new VM template
   */
  uploadTemplate: async (data: VMTemplateUploadData): Promise<VMTemplate> => {
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('name', data.name)
    formData.append('version', data.version)
    formData.append('category', data.category)
    formData.append('format', data.format)

    if (data.description) formData.append('description', data.description)
    if (data.os_type) formData.append('os_type', data.os_type)
    if (data.os_variant) formData.append('os_variant', data.os_variant)
    if (data.os_version) formData.append('os_version', data.os_version)
    if (data.min_disk_gb !== undefined) formData.append('min_disk_gb', data.min_disk_gb.toString())
    if (data.min_memory_mb !== undefined)
      formData.append('min_memory_mb', data.min_memory_mb.toString())
    if (data.min_cpu_cores !== undefined)
      formData.append('min_cpu_cores', data.min_cpu_cores.toString())
    if (data.release_notes) formData.append('release_notes', data.release_notes)
    if (data.tags) formData.append('tags', data.tags)
    if (data.default_disk_interface)
      formData.append('default_disk_interface', data.default_disk_interface)
    if (data.default_net_interface)
      formData.append('default_net_interface', data.default_net_interface)
    if (data.supports_cloud_init !== undefined)
      formData.append('supports_cloud_init', data.supports_cloud_init.toString())
    if (data.supports_qemu_agent !== undefined)
      formData.append('supports_qemu_agent', data.supports_qemu_agent.toString())
    if (data.set_as_active !== undefined)
      formData.append('set_as_active', data.set_as_active.toString())
    if (data.set_as_featured !== undefined)
      formData.append('set_as_featured', data.set_as_featured.toString())

    return apiClient.postFormData('/api/v1/vm-templates/upload', formData)
  },

  // ===== Presigned Upload Flow =====

  /**
   * Request a presigned URL for direct upload to R2
   */
  requestUpload: (data: PresignedUploadRequest): Promise<PresignedUploadResponse> => {
    return apiClient.post('/api/v1/vm-templates/request-upload', data)
  },

  /**
   * Confirm upload after direct PUT to R2
   */
  confirmUpload: (data: ConfirmUploadData): Promise<VMTemplate> => {
    return apiClient.post('/api/v1/vm-templates/confirm-upload', data)
  },

  // ===== Multipart Upload Flow (#934) =====
  //
  // Os três endpoints que o backend ganhou na PR #992. A ORQUESTRAÇÃO (hash em
  // streaming, PUT único × multipart, retry, abort em toda saída) mora em
  // `vmTemplateUpload.ts` — fora do barrel de `services`, porque arrasta
  // `hash-wasm`, que só o portal ADM (o único que sobe template) declara.

  /**
   * Abre um multipart upload e recebe uma presigned URL por parte.
   *
   * O backend assina UMA URL por parte, em série; com partes de 100 MiB num
   * arquivo de 15 GB são 150 assinaturas. Os 30s default do axios cobrem, mas
   * um cliente que desistir DEPOIS de o backend ter criado o multipart deixa um
   * `upload_id` que ninguém recebeu — o "terceiro caminho de órfão" da doc
   * (`docs/R2_CORS_UPLOAD_TEMPLATE.md`). Por isso o timeout é explícito e largo.
   */
  requestMultipartUpload: (
    data: MultipartUploadRequest,
    config?: { timeout?: number },
  ): Promise<MultipartUploadResponse> => {
    return apiClient.post('/api/v1/vm-templates/request-multipart-upload', data, config)
  },

  /**
   * Fecha o multipart no R2 e cria o registro do template.
   *
   * Para um objeto de vários GiB o backend leva minutos (o `complete` do R2 +
   * `head_object` + commit), então quem chama passa um timeout largo: um timeout
   * curto aqui não cancela nada no servidor — só transforma o caminho feliz num
   * desfecho desconhecido.
   */
  completeMultipartUpload: (
    data: CompleteMultipartUploadData,
    config?: { timeout?: number },
  ): Promise<VMTemplate> => {
    return apiClient.post('/api/v1/vm-templates/complete-multipart-upload', data, config)
  },

  /**
   * Aborta o multipart e libera as partes já enviadas no R2.
   *
   * Nunca amarre esta chamada ao `AbortSignal` do upload: cancelar o upload é
   * exatamente quando ela precisa correr.
   */
  abortMultipartUpload: (data: AbortMultipartUploadData): Promise<AbortMultipartUploadResponse> => {
    return apiClient.post('/api/v1/vm-templates/abort-multipart-upload', data)
  },

  /**
   * List all VM templates with optional filters
   */
  listTemplates: (params?: {
    category?: VMTemplateCategory
    is_active?: boolean
    is_featured?: boolean
  }): Promise<VMTemplateListResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.category) queryParams.append('category', params.category)
    if (params?.is_active !== undefined)
      queryParams.append('is_active', params.is_active.toString())
    if (params?.is_featured !== undefined)
      queryParams.append('is_featured', params.is_featured.toString())

    const queryString = queryParams.toString()
    return apiClient.get(`/api/v1/vm-templates${queryString ? `?${queryString}` : ''}`)
  },

  /**
   * Get a specific template by ID
   */
  getTemplate: (id: string): Promise<VMTemplate> => {
    return apiClient.get(`/api/v1/vm-templates/${id}`)
  },

  /**
   * Activate a template
   */
  activateTemplate: (id: string, setAsFeatured?: boolean): Promise<VMTemplate> => {
    return apiClient.post(`/api/v1/vm-templates/${id}/activate`, {
      set_as_featured: setAsFeatured || false,
    })
  },

  /**
   * Deactivate a template
   */
  deactivateTemplate: (id: string): Promise<VMTemplate> => {
    return apiClient.post(`/api/v1/vm-templates/${id}/deactivate`)
  },

  /**
   * Update template metadata
   */
  updateTemplate: (id: string, data: VMTemplateUpdate): Promise<VMTemplate> => {
    return apiClient.patch(`/api/v1/vm-templates/${id}`, data)
  },

  /**
   * Delete a template
   */
  deleteTemplate: (id: string): Promise<void> => {
    return apiClient.delete(`/api/v1/vm-templates/${id}`)
  },

  // ===== Download Operations =====

  /**
   * Get download URL for a template
   */
  getDownloadUrl: (id: string): Promise<VMTemplateDownloadUrlResponse> => {
    return apiClient.get(`/api/v1/vm-templates/${id}/download-url`)
  },

  /**
   * Request template download to a cluster
   */
  downloadToCluster: (
    templateId: string,
    clusterId: string,
    data: VMTemplateDownloadRequest,
  ): Promise<VMTemplateDownloadResponse> => {
    return apiClient.post(
      `/api/v1/vm-templates/${templateId}/download-to-cluster/${clusterId}`,
      data,
    )
  },
}

// ===== Helper Functions =====

/**
 * Get category display label
 */
export const getCategoryLabel = (category: VMTemplateCategory): string => {
  const labels: Record<VMTemplateCategory, string> = {
    gateway: 'Cloud Gateway',
    os_linux: 'Linux OS',
    os_windows: 'Windows OS',
    appliance: 'Appliance',
  }
  return labels[category] || category
}

/**
 * Get category color for badges
 */
export const getCategoryColor = (category: VMTemplateCategory): string => {
  const colors: Record<VMTemplateCategory, string> = {
    gateway: 'bg-purple-100 text-purple-800',
    os_linux: 'bg-green-100 text-green-800',
    os_windows: 'bg-blue-100 text-blue-800',
    appliance: 'bg-orange-100 text-orange-800',
  }
  return colors[category] || 'bg-gray-100 text-gray-800'
}

/**
 * Get format display label
 */
export const getFormatLabel = (format: VMTemplateFormat): string => {
  const labels: Record<VMTemplateFormat, string> = {
    qcow2: 'QCOW2',
    iso: 'ISO',
    ova: 'OVA',
    raw: 'RAW',
  }
  return labels[format] || format.toUpperCase()
}

/**
 * Get format color for badges
 */
export const getFormatColor = (format: VMTemplateFormat): string => {
  const colors: Record<VMTemplateFormat, string> = {
    qcow2: 'bg-indigo-100 text-indigo-800',
    iso: 'bg-yellow-100 text-yellow-800',
    ova: 'bg-pink-100 text-pink-800',
    raw: 'bg-gray-100 text-gray-800',
  }
  return colors[format] || 'bg-gray-100 text-gray-800'
}

/**
 * Format file size to human-readable string
 */
export const formatTemplateSize = (bytes: number | null | undefined): string => {
  // Template importado do cluster não tem artefato no R2, logo não tem tamanho.
  if (bytes === null || bytes === undefined) return '—'
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Get OS icon class based on os_type
 */
export const getOSIcon = (osType: string | null): string => {
  if (!osType) return 'file'

  switch (osType.toLowerCase()) {
    case 'linux':
      return 'terminal'
    case 'windows':
      return 'window'
    case 'bsd':
      return 'shield'
    default:
      return 'file'
  }
}

/**
 * Get category icon
 */
export const getCategoryIcon = (category: VMTemplateCategory): string => {
  const icons: Record<VMTemplateCategory, string> = {
    gateway: 'router',
    os_linux: 'terminal',
    os_windows: 'laptop',
    appliance: 'box',
  }
  return icons[category] || 'file'
}

export default vmTemplatesApi
